import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { AutoFixHigh as AutoIcon, Close as CloseIcon, PaymentsOutlined as PayIcon } from '@mui/icons-material';
import { comptoirPaiementsAPI } from '../../../services/api';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { useComptoirDus } from '../../../hooks/queries/useComptoir';
import { ComptoirPaiement, ComptoirService } from '../../../types/comptoir';
import { COLOR, NumberField, dh, num, primaryBtnSx, r2, today } from '../ui';

const MODES = ['Espèces', 'Chèque', 'Virement', 'Carte', 'Effet'];
/** Pre-filled amounts: 680, 2093.80 (never 680.00 nor 2093.8). */
const amountText = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(2));
const NO_DUS: ComptoirService[] = []; // stable while the query is idle, so the auto-split effect doesn't loop

interface Props {
  open: boolean;
  clientId: number | null;
  clientNom?: string | null;
  serviceId?: number | null; // the service the user came from: allocated first
  onClose: () => void;
  onSaved: (p: ComptoirPaiement) => void;
}

/** Encaisser: one voucher (BON), split over the client's services that still owe money. */
const EncaisserDialog: React.FC<Props> = ({ open, clientId, clientNom, serviceId, onClose, onSaved }) => {
  const { data: dusRaw = NO_DUS, isLoading } = useComptoirDus(clientId, open);
  const dus = useMemo(
    () => dusRaw.slice().sort((a, b) => Number(b.id === serviceId) - Number(a.id === serviceId)),
    [dusRaw, serviceId],
  );
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState('Espèces');
  const [ref, setRef] = useState('');
  const [note, setNote] = useState('');
  const [alloc, setAlloc] = useState<Record<number, string>>({});
  const [manual, setManual] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverErr, setServerErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMontant(''); setDate(today()); setMode('Espèces'); setRef(''); setNote('');
    setAlloc({}); setManual(false); setSubmitted(false); setServerErr('');
  }, [open, clientId]);

  const totalDu = r2(dus.reduce((a, s) => a + num(s.reste), 0));

  // Pre-fill with what the service still owes: the rest after the avance, or the whole total if nothing was paid
  const prefilled = useRef(false);
  useEffect(() => {
    if (!open) { prefilled.current = false; return; }
    if (prefilled.current || isLoading || !dus.length) return;
    prefilled.current = true;
    const current = dus.find((s) => s.id === serviceId);
    const v = r2(current ? num(current.reste) : totalDu);
    if (v > 0) setMontant(amountText(v));
  }, [open, isLoading, dus, serviceId, totalDu]);

  /** Fills the services in order (the one we came from first) until the amount is used up. */
  const autoAllocate = (amount: number) => {
    let left = r2(amount);
    const next: Record<number, string> = {};
    dus.forEach((s) => {
      const a = r2(Math.min(num(s.reste), Math.max(0, left)));
      next[s.id] = a > 0 ? amountText(a) : '';
      left = r2(left - a);
    });
    setAlloc(next);
  };

  useEffect(() => {
    if (open && !manual) autoAllocate(num(montant));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, montant, dus, manual]);

  const somme = r2(Object.values(alloc).reduce((a, v) => a + num(v), 0));
  const amount = r2(num(montant));
  const over = dus.find((s) => num(alloc[s.id]) > num(s.reste) + 0.005);

  const errors: { montant?: string; alloc?: string; date?: string } = {};
  if (amount <= 0) errors.montant = 'Indiquez le montant reçu.';
  else if (amount > totalDu + 0.005) errors.montant = `Le client ne doit que ${dh(totalDu)}.`;
  if (!date) errors.date = 'Indiquez la date.';
  if (over) errors.alloc = `${over.reference} : le montant dépasse le reste dû (${dh(over.reste)}).`;
  else if (amount > 0 && Math.abs(somme - amount) >= 0.01) errors.alloc = `La répartition (${dh(somme)}) doit être égale au montant reçu (${dh(amount)}).`;
  const show = (k: keyof typeof errors) => (submitted ? errors[k] : undefined);

  const handleSave = async () => {
    setSubmitted(true);
    setServerErr('');
    if (Object.keys(errors).length || !clientId) return;
    setSaving(true);
    try {
      const p = await comptoirPaiementsAPI.create({
        client_id: clientId, date, montant: amount, mode,
        reference_paiement: ref.trim() || null, note: note.trim() || null,
        allocations: dus.filter((s) => num(alloc[s.id]) > 0).map((s) => ({ service_id: s.id, montant: r2(num(alloc[s.id])) })),
      });
      onSaved(p);
    } catch (e: any) {
      setServerErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="md" fullWidth aria-labelledby="comptoir-pay-title" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: alpha('#2E7D32', 0.12), color: '#2E7D32', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PayIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography id="comptoir-pay-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Encaisser un paiement</Typography>
          <Typography variant="body2" color="text.secondary">{clientNom} · reste dû <b>{dh(totalDu)}</b></Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Fermer"><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ pt: '20px !important' }}>
        {serverErr && <Alert severity="error" sx={{ mb: 2 }}>{serverErr}</Alert>}
        {isLoading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : dus.length === 0 ? (
          <Alert severity="success">Ce client ne doit rien : tous ses services sont soldés.</Alert>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <NumberField label="Montant reçu" required fullWidth autoFocus unit="DH" value={montant}
                  onChange={(v) => { setMontant(v); setServerErr(''); }}
                  error={!!show('montant')} helperText={show('montant') || ' '} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField label="Date" type="date" fullWidth required InputLabelProps={{ shrink: true }} value={date}
                  inputProps={{ max: today() }} onChange={(e) => setDate(e.target.value)} error={!!show('date')} helperText={show('date') || ' '} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField select label="Mode de paiement" fullWidth value={mode} onChange={(e) => setMode(e.target.value)}
                  helperText={mode === 'Espèces' ? 'Entre dans le journal de caisse' : ' '}>
                  {MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Référence" fullWidth placeholder={mode === 'Espèces' ? 'Facultatif' : 'n° chèque, virement…'} value={ref} onChange={(e) => setRef(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Note" fullWidth placeholder="Facultatif" value={note} onChange={(e) => setNote(e.target.value)} />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ mt: 2.5, borderRadius: 2, overflow: 'hidden', borderColor: show('alloc') ? 'error.main' : 'divider' }}>
              <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.25, bgcolor: alpha(COLOR, 0.05) }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>Répartition sur les services</Typography>
                <Button size="small" startIcon={<AutoIcon />} onClick={() => { setManual(false); autoAllocate(amount); }} sx={{ color: COLOR }}>
                  Répartir automatiquement
                </Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Service</TableCell><TableCell>Désignation</TableCell>
                    <TableCell align="right">Reste dû</TableCell><TableCell align="right" width={170}>Montant affecté</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dus.map((s) => {
                    const bad = num(alloc[s.id]) > num(s.reste) + 0.005;
                    return (
                      <TableRow key={s.id} selected={s.id === serviceId}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{s.reference}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}><Typography variant="body2" noWrap>{s.titre}</Typography></TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{dh(s.reste)}</TableCell>
                        <TableCell align="right">
                          <NumberField size="small" unit="DH" value={alloc[s.id] || ''} error={bad}
                            onChange={(v) => { setManual(true); setAlloc((a) => ({ ...a, [s.id]: v })); }}
                            inputProps={{ 'aria-label': `Montant affecté à ${s.reference}` }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between',
                bgcolor: amount > 0 && !errors.alloc ? alpha('#2E7D32', 0.06) : undefined }}>
                <Typography variant="body2">Affecté : <b>{dh(somme)}</b> sur <b>{dh(amount)}</b></Typography>
                {show('alloc') && <Typography variant="body2" color="error">{show('alloc')}</Typography>}
              </Box>
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>Annuler</Button>
        {dus.length > 0 && (
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ ...primaryBtnSx, minWidth: 220 }}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <PayIcon />}>
            {saving ? 'Enregistrement…' : 'Enregistrer et émettre le bon'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EncaisserDialog;
